import { type Placeholder, PWysiwygTextInputFieldFormat } from '../types'

export type WysiwygMarkdownOptions = {
  placeholders: Placeholder[]
  variableFormat: PWysiwygTextInputFieldFormat
}

/**
 * Block rendering a line the user left empty between two paragraphs. Markdown separates blocks by a
 * single newline, every newline above that one is such an empty line.
 */
const BLANK_LINE_BLOCK = '<div><br></div>'

const SAFE_URL_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:']

const stripSchemeNoise = (url: string): string => {
  return Array.from(url)
    .filter((character) => {
      const code = character.charCodeAt(0)
      return code > 0x20 && code !== 0x7f
    })
    .join('')
}

export const sanitizeUrl = (url: string): string => {
  const scheme = stripSchemeNoise(url)
    .toLowerCase()
    .match(/^[a-z][a-z0-9+.-]*:/)

  // No scheme at all means a relative url, which cannot carry an executable one.
  if (scheme === null) {
    return url
  }

  return SAFE_URL_SCHEMES.includes(scheme[0]) ? url : 'about:blank'
}

const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

const toBlockHtml = (block: string): string => {
  if (
    block.startsWith('<h') ||
    block.startsWith('<ul>') ||
    block.startsWith('<ol>') ||
    block.startsWith('<pre>')
  ) {
    return block
  }
  return `<div>${block.replace(/\n/g, '<br>')}</div>`
}

export const markdownToHtml = (
  markdown: string | null | undefined,
  options: WysiwygMarkdownOptions,
): string => {
  if (!markdown) {
    return ''
  }

  let html = escapeHtml(markdown.trim())

  options.placeholders.forEach((p) => {
    const escapedKey = p.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    let chipLabel: string
    let regexPattern: string
    switch (options.variableFormat) {
      case PWysiwygTextInputFieldFormat.CURLY:
        chipLabel = `{${p.label}}`
        regexPattern = `\\{${escapedKey}\\}`
        break
      case PWysiwygTextInputFieldFormat.BRACKETS:
      default:
        chipLabel = `[[${p.label}]]`
        regexPattern = `\\[\\[${escapedKey}\\]\\]`
        break
    }
    const placeholderHtml = `<span class="p-wysiwyg-placeholder-chip" contenteditable="false" data-key="${p.key}">${chipLabel}</span>`
    const regex = new RegExp(regexPattern, 'g')
    html = html.replace(regex, placeholderHtml)
  })

  html = html
    .replace(/^###### (.*)$/gm, '<h6>$1</h6>')
    .replace(/^##### (.*)$/gm, '<h5>$1</h5>')
    .replace(/^#### (.*)$/gm, '<h4>$1</h4>')
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/^[ \t]*-[ \t]*$/gm, '<li>&nbsp;</li>')
    .replace(/^[ \t]*-[ \t]+(.*)$/gm, '<li>$1</li>')
    .replace(/(?:<li>.*<\/li>\n?)+/g, (match) => {
      if (match.includes('<ul>') || match.includes('<ol>')) return match
      return `<ul>${match.trim().split('\n').join('')}</ul>`
    })
    .replace(/^[ \t]*\d+\.[ \t]*$/gm, '<li data-ol>&nbsp;</li>')
    .replace(/^[ \t]*\d+\.[ \t]+(.*)$/gm, '<li data-ol>$1</li>')
    .replace(/(?:<li data-ol>.*<\/li>\n?)+/g, (match) => {
      return `<ol>${match
        .trim()
        .replace(/ data-ol/g, '')
        .split('\n')
        .join('')}</ol>`
    })
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, url) => {
      return `<a href="${sanitizeUrl(url)}">${text}</a>`
    })

  html = html
    .split(/(\n{2,})/)
    .map((part, index) => {
      // Odd indexes hold the separators captured by the split regex.
      if (index % 2 === 1) {
        return BLANK_LINE_BLOCK.repeat(part.length - 1)
      }
      return toBlockHtml(part)
    })
    .join('')

  return html.trim()
}

export const htmlToMarkdown = (html: string | null, options: WysiwygMarkdownOptions): string => {
  if (!html) {
    return ''
  }

  const div = document.createElement('div')
  div.innerHTML = html
  return processNode(div, options).trim()
}

const BLOCK_TAGS = ['DIV', 'P', 'UL', 'OL']

const isBlockElement = (node: Node): boolean => {
  return node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.includes((node as HTMLElement).tagName)
}

const isBlankBlock = (element: HTMLElement): boolean => {
  const children = Array.from(element.childNodes).filter(
    (child) => child.nodeType !== Node.TEXT_NODE || (child.textContent ?? '') !== '',
  )
  return (
    children.length === 0 ||
    (children.length === 1 && (children[0] as HTMLElement).tagName === 'BR')
  )
}

const appendBlock = (content: string, part: string): string => {
  return content.endsWith('\n') && part.startsWith('\n') ? content + part.slice(1) : content + part
}

const wrapInline = (content: string, marker: string): string => {
  return content
    .split('\n')
    .map((line) => (line === '' ? line : `${marker}${line}${marker}`))
    .join('\n')
}

const processNode = (node: Node, options: WysiwygMarkdownOptions): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || ''
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement

    if (element.classList.contains('p-wysiwyg-placeholder-chip')) {
      const key = element.dataset.key || ''
      if (!key) {
        return ''
      }
      return options.variableFormat === PWysiwygTextInputFieldFormat.CURLY
        ? `{${key}}`
        : `[[${key}]]`
    }

    let content = ''
    let index = 1
    element.childNodes.forEach((child) => {
      const part = processNode(child, options)
      content = isBlockElement(child) ? appendBlock(content, part) : content + part
    })

    switch (element.tagName) {
      case 'B':
      case 'STRONG':
        return wrapInline(content, '**')
      case 'I':
      case 'EM':
        return wrapInline(content, '*')
      case 'U':
        return wrapInline(content, '__')
      case 'A':
        return `[${content}](${element.getAttribute('href')})`
      case 'LI':
        return content.trim() || ''
      case 'UL':
        return (
          '\n' +
          Array.from(element.childNodes)
            .map((child) => {
              if (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === 'LI') {
                return `- ${processNode(child, options)}` + '\n'
              }
              return processNode(child, options)
            })
            .join('')
        )
      case 'OL':
        return (
          '\n' +
          Array.from(element.childNodes)
            .map((child) => {
              if (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === 'LI') {
                return `${index++}. ${processNode(child, options)}` + '\n'
              }
              return processNode(child, options)
            })
            .join('')
        )
      case 'BR':
        return '\n'
      case 'DIV':
      case 'P':
        return isBlankBlock(element) ? '\n\n' : `\n${content}`
      case 'H1':
        return `# ${content}`
      case 'H2':
        return `## ${content}`
      case 'H3':
        return `### ${content}`
      case 'H4':
        return `#### ${content}`
      case 'H5':
        return `##### ${content}`
      case 'H6':
        return `###### ${content}`
      default:
        return content
    }
  }
  return ''
}
